import type { Bula, Dose, Produto, Regra, Toxico } from "@vetbolso/contracts";
import { espMatch } from "./especies.ts";

export type Acerto = { tipo: "bula" | "dose" | "tox" | "prod" | "regra"; id: string; t: string; s: string };
type Base = { bulario?: Bula[]; doses?: Dose[]; toxicos?: Toxico[]; produtos?: Produto[]; regras?: Regra[] };

const semAcento = (s: string) => s.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase();

/**
 * Busca da tela inicial: bulário e doses da espécie, produtos, tóxicos que valem
 * para ela e todas as regras. Um fármaco com bulário aparece uma vez só, pela bula.
 */
export function buscar(db: Base, consulta: string, especie: string, limite = 8): Acerto[] {
  const q = semAcento(consulta.trim());
  if (q.length < 2) return [];
  const tem = (s: string) => semAcento(s).includes(q);
  const hits: Acerto[] = [];
  const comBula = new Set<string>();
  for (const b of db.bulario ?? [])
    if (espMatch(b, especie) && tem(`${b.farmaco} ${b.classe} ${(b.apresentacoes ?? []).map((a) => a.nome).join(" ")}`)) {
      hits.push({ tipo: "bula", id: b.id, t: b.farmaco, s: `Bulário · ${b.classe}` });
      comBula.add(b.id);
    }
  for (const d of db.doses ?? [])
    if (d.especie === especie && !(d.bula && comBula.has(d.bula)) && tem(`${d.farmaco} ${d.classe || ""}`))
      hits.push({ tipo: "dose", id: d.id, t: d.farmaco, s: `Dose · ${d.classe || ""}` });
  for (const t of db.toxicos ?? [])
    if (espMatch(t, especie) && tem(`${t.agente} ${t.sinonimos || ""}`)) hits.push({ tipo: "tox", id: t.id, t: t.agente, s: "Tóxico" });
  for (const p of db.produtos ?? [])
    if (p.especies.includes(especie as never) && tem(`${p.produto} ${p.farmaco}`))
      hits.push({ tipo: "prod", id: p.id, t: p.produto, s: `Produto · ${p.farmaco}` });
  for (const r of db.regras ?? [])
    if (tem(`${r.titulo} ${r.resumo || ""}`)) hits.push({ tipo: "regra", id: r.id, t: r.titulo, s: "Regra" });
  return hits.slice(0, limite);
}

/** Doses do fármaco de uma bula, na ordem das espécies. */
export function dosesDaBula(doses: Dose[], bulaId: string) {
  const ordem = ["cao", "gato", "bovino", "equino"];
  return doses.filter((d) => d.bula === bulaId).sort((a, b) => ordem.indexOf(a.especie) - ordem.indexOf(b.especie));
}
